import { IMinAppBuild } from '@shared/models/application-behavior-model';

/**
 * The minimum-version gate's rules (kzd-ac AC-R6, `docs/kz-appcheck/PLAN-appcheck.md` §6 "Old builds"): pure, so the
 * service and its specs call the same path.
 *
 * FAIL OPEN, always: a config that is missing or malformed, a build this device cannot read, an unknown platform and
 * the web all answer `'ok'`. Only a readable build below a readable minimum answers `'update'`. A gate that locked a
 * register out on a typo would be worse than the risk it guards against.
 */
export type KzBuildVerdict = 'ok' | 'update';

/** The shells the gate applies to (`kzDetectPlatform()`); the web is never gated — hosting always serves the latest. */
export const KZ_GATED_PLATFORMS: ReadonlySet<string> = new Set(['android', 'ios', 'electron']);

/**
 * The last minimum this device read, per backend project (`kz.min-app-build.v1:<projectId>`): a device that saw the
 * gate once stays gated offline, after a restart too. Public configuration only — nothing about a person or a business.
 */
export const MIN_APP_BUILD_KEY_PREFIX = 'kz.min-app-build.v1:';

/** A native build number (`versionCode` / `CFBundleVersion`): a whole number ≥ 0, as a number or digits; else `null`. */
export function buildNumber(v: unknown): number | null {
  if (typeof v === 'number') {
    return Number.isSafeInteger(v) && v >= 0 ? v : null;
  }
  if (typeof v === 'string' && /^\d{1,15}$/.test(v.trim())) {
    return Number(v.trim());
  }
  return null;
}

/**
 * The highest native build number a build made OUTSIDE CI carries (kzd-ac review MV-1). CI sets the Android
 * `versionCode` and the iOS `CFBundleVersion` from `KOOMZO_VERSION_CODE` (`$CI_PIPELINE_IID`); without it
 * `android/app/build.gradle` and `scripts/ios/version-xcconfig.js` fall back to 1. The dev tablet's debug APK is such a
 * build: compared as 1, a minimum pushed to dev would strand it behind "Update required" with no build to update to.
 * So this build's number at or below it is UNKNOWN (fail open). Published builds are far above it (Android 995+).
 */
export const LOCAL_BUILD_NUMBER_MAX = 1;

/** THIS device's native build number: `buildNumber`, but a local build's fallback (≤ `LOCAL_BUILD_NUMBER_MAX`) is `null`. */
export function thisNativeBuildNumber(v: unknown): number | null {
  const n = buildNumber(v);
  return n !== null && n > LOCAL_BUILD_NUMBER_MAX ? n : null;
}

interface ParsedSemver {
  readonly core: readonly [number, number, number];
  readonly pre: readonly string[];
}

/** `1`, `1.2`, `1.2.3`, a leading `v`, a `-pre.release` and a `+build` (ignored, per semver §10); else `null`. */
function parseSemver(v: unknown): ParsedSemver | null {
  if (typeof v !== 'string') {
    return null;
  }
  const m = /^v?(\d{1,9})(?:\.(\d{1,9}))?(?:\.(\d{1,9}))?(?:-([0-9A-Za-z.-]+))?(?:\+[0-9A-Za-z.-]+)?$/.exec(v.trim());
  if (!m) {
    return null;
  }
  const pre = m[4] ? m[4].split('.') : [];
  if (pre.some((p) => p === '')) {
    return null;
  }
  return { core: [Number(m[1]), Number(m[2] ?? 0), Number(m[3] ?? 0)], pre };
}

/** semver §11: numeric identifiers compare as numbers and rank below alphanumeric ones. */
function comparePre(a: readonly string[], b: readonly string[]): number {
  if (!a.length || !b.length) {
    return a.length === b.length ? 0 : a.length ? -1 : 1; // a pre-release ranks below its release
  }
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if (i >= a.length) return -1;
    if (i >= b.length) return 1;
    const x = a[i];
    const y = b[i];
    const xn = /^\d+$/.test(x);
    const yn = /^\d+$/.test(y);
    if (xn && yn) {
      const d = Number(x) - Number(y);
      if (d !== 0) return d < 0 ? -1 : 1;
    } else if (xn !== yn) {
      return xn ? -1 : 1;
    } else if (x !== y) {
      return x < y ? -1 : 1;
    }
  }
  return 0;
}

/** `-1`, `0` or `1` for two semvers (the Electron app's version); `null` when either does not parse. */
export function compareSemver(a: unknown, b: unknown): -1 | 0 | 1 | null {
  const x = parseSemver(a);
  const y = parseSemver(b);
  if (!x || !y) {
    return null;
  }
  for (let i = 0; i < 3; i++) {
    if (x.core[i] !== y.core[i]) {
      return x.core[i] < y.core[i] ? -1 : 1;
    }
  }
  return comparePre(x.pre, y.pre) as -1 | 0 | 1;
}

/**
 * The config's `minAppBuild` as the gate reads it: only the well-formed fields (`android`/`ios` whole numbers,
 * `desktop` a semver); `null` when none is. A malformed field is dropped, so it never gates (fail open).
 */
export function sanitizeMinAppBuild(raw: unknown): IMinAppBuild | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const out: { android?: number; ios?: number; desktop?: string } = {};
  const android = buildNumber(r['android']);
  if (android !== null) out.android = android;
  const ios = buildNumber(r['ios']);
  if (ios !== null) out.ios = ios;
  if (typeof r['desktop'] === 'string' && parseSemver(r['desktop'])) out.desktop = (r['desktop'] as string).trim();
  return Object.keys(out).length ? out : null;
}

/**
 * The decision (AC-R6): `(platform, thisBuild, minAppBuild) → 'ok' | 'update'`.
 * - `android` / `ios`: `thisBuild` is the native build number (`App.getInfo().build`), compared with `min.android` /
 *   `min.ios` as whole numbers;
 * - `electron`: `thisBuild` is the app's semver (`App.getInfo().version`), compared with `min.desktop`;
 * - `web` and anything else: never gated.
 * Fail open: no minimum for the shell, or either side unreadable, is `'ok'` — and a native build numbered 1 or less
 * (a build made outside CI, `LOCAL_BUILD_NUMBER_MAX`) counts as unreadable.
 */
export function minAppBuildVerdict(platform: string, thisBuild: unknown, minAppBuild: unknown): KzBuildVerdict {
  const min = sanitizeMinAppBuild(minAppBuild);
  if (!min) {
    return 'ok';
  }
  switch (platform) {
    case 'android':
    case 'ios': {
      const need = buildNumber(min[platform]);
      const have = thisNativeBuildNumber(thisBuild); // a local build (versionCode 1) is unknown: never gated (MV-1)
      return need !== null && have !== null && have < need ? 'update' : 'ok';
    }
    case 'electron':
      return compareSemver(thisBuild, min.desktop) === -1 ? 'update' : 'ok';
    default:
      return 'ok';
  }
}

/** The minimum this shell needs, as shown on the screen ("Needed: 990"); `null` when none. */
export function requiredBuildFor(platform: string, minAppBuild: IMinAppBuild | null): string | null {
  if (!minAppBuild) return null;
  const v = platform === 'electron' ? minAppBuild.desktop : platform === 'android' || platform === 'ios' ? minAppBuild[platform] : undefined;
  return v === undefined ? null : String(v);
}

/** The last minimum this device read for `project`; `null` when none, unreadable, or storage is blocked. Never throws. */
export function readRememberedMinAppBuild(project: string | null | undefined, storage: Storage | null): IMinAppBuild | null {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(MIN_APP_BUILD_KEY_PREFIX + (project || 'default'));
    return raw ? sanitizeMinAppBuild(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * Keep the minimum just read for the next start; `null` (the config has none) forgets it — the owner lifted the gate.
 * Never throws: a full or blocked storage only loses the offline memory, never the gate of this run.
 */
export function rememberMinAppBuild(project: string | null | undefined, min: IMinAppBuild | null, storage: Storage | null): void {
  if (!storage) {
    return;
  }
  const key = MIN_APP_BUILD_KEY_PREFIX + (project || 'default');
  try {
    if (min) {
      storage.setItem(key, JSON.stringify(min));
    } else {
      storage.removeItem(key);
    }
  } catch {
    /* full or blocked storage */
  }
}

/** One installer as the desktop releases manifest lists it (`.gitlab/ci/electron.yml`, `deploy:electron:<env>`). */
export interface KzDesktopInstaller {
  readonly version: string;
  readonly url: string;
}

/**
 * The desktop releases manifest of this backend project: `gs://<projectId>-electron-downloads/releases/electron/
 * manifest.json`, public-read, written by CI at each desktop release (`{ version, win: { url }, mac: { url } }`). There
 * is no in-app updater (`electron/electron-builder.config.js`: `publish: null`); this is where installers are listed.
 */
export function desktopManifestUrl(projectId: string | null | undefined): string | null {
  return projectId ? `https://storage.googleapis.com/${projectId}-electron-downloads/releases/electron/manifest.json` : null;
}

/** The computer this desktop app runs on, from the user agent: `win`, `mac`, or `null` (Linux, unknown — no installer). */
export function desktopOs(userAgent: string | null | undefined): 'win' | 'mac' | null {
  const ua = userAgent ?? '';
  return /Windows/i.test(ua) ? 'win' : /Mac/i.test(ua) ? 'mac' : null;
}

/**
 * The installer to offer, from the manifest: the one for this OS, and only when its version meets the minimum — an
 * installer still below it would reinstall a build the gate refuses. `null` otherwise (the screen then says to get the
 * new version without a button).
 */
export function desktopInstallerFrom(manifest: unknown, os: 'win' | 'mac' | null, minDesktop: string | null | undefined): KzDesktopInstaller | null {
  if (!os || !manifest || typeof manifest !== 'object') {
    return null;
  }
  const m = manifest as Record<string, unknown>;
  const entry = m[os] as Record<string, unknown> | null | undefined;
  const version = m['version'];
  const url = entry && typeof entry === 'object' ? entry['url'] : null;
  if (typeof version !== 'string' || typeof url !== 'string' || !/^https:\/\//.test(url)) {
    return null;
  }
  const cmp = compareSemver(version, minDesktop);
  if (minDesktop && cmp !== 0 && cmp !== 1) {
    return null;
  }
  return { version, url };
}
